import { useEffect, useState } from "react";
import { Timeline } from "antd";
import type { DeliveryVo, ShipVo } from "@zhihaoscm/entity";

import { shipService } from "@/api/services/shipService";
import { SHIP_WAY_BILL_TYPE, WAYBILL_STATE } from "@/meta/logistics/waybill";
// 物流空态必须沿用旧版 80×51 位图；新版通用空态图的尺寸和抗锯齿不同，会造成视觉回归。
import notDataImage from "../../../../../../../../old-admin-ui/src/views/assets/images/notData.png";
import type { TradeOrderData } from "../TradeOrderComponentPrimitives";
import MapVideoWrap from "./mapVideoWrap";

export interface LogisticsTrackingProps {
    items?: TradeOrderData[];
    deliveryVo?: DeliveryVo;
    onSelect?: (item: TradeOrderData) => void;
}

const SUPERVISION_STEPS = [
    [WAYBILL_STATE.AWAITING_DEPARTURE.code, "船舶处于装货阶段，准备发航"],
    [WAYBILL_STATE.DURING_TRANSPORTATION.code, "船舶已发航，正在运输中"],
    [WAYBILL_STATE.TO_BE_UNLOADED.code, "船舶已到港，准备卸货"],
    [WAYBILL_STATE.COMPLETED.code, "已卸货清仓完毕，船运单已完成"]
] as const;
const MATCHING_STEPS = [
    [WAYBILL_STATE.UNCOMPLETED.code, "已开始船运单履约流程"],
    [WAYBILL_STATE.COMPLETED.code, "船运单已完成"]
] as const;

/** 物流追踪按 Vue 的运单类型和当前状态截取已发生节点，未关联运单时展示专用空态。 */
export default function LogisticsTracking({ deliveryVo }: LogisticsTrackingProps) {
    const [shipVo, setShipVo] = useState<ShipVo>();
    const waybill = deliveryVo?.transportOrderShip;
    const shipId = deliveryVo?.delivery?.shipId;
    const steps =
        Number(waybill?.serviceType) === SHIP_WAY_BILL_TYPE.SUPERVISION.code ? SUPERVISION_STEPS : MATCHING_STEPS;
    const activeIndex = steps.findIndex(([state]) => state === Number(waybill?.state));
    const visibleSteps = activeIndex >= 0 ? steps.slice(0, activeIndex + 1).reverse() : [];
    // 地图渲染只依赖船舶 id；完整 ShipVo 尚未返回时先使用提货单快照，避免地图板块整体消失。
    const mapShipVo =
        shipVo ||
        (shipId
            ? ({
                  ship: { ...(deliveryVo?.delivery?.shipInfo || {}), id: shipId },
                  devices: []
              } as unknown as ShipVo)
            : undefined);

    useEffect(() => {
        let active = true;
        if (!shipId) {
            setShipVo(undefined);
            return () => {
                active = false;
            };
        }
        /** Vue 会先通过提货单 shipId 获取完整 ShipVo，再由地图组件请求实时船位。 */
        void shipService
            .getVo(shipId)
            .then(value => {
                if (active) setShipVo(value as ShipVo);
            })
            .catch(() => {
                if (active) setShipVo(undefined);
            });
        return () => {
            active = false;
        };
    }, [shipId]);

    return (
        <section className="section-card mb-4 trade-order-logistics-tracking">
            <div className="section-title mb-4">物流追踪</div>
            <div className="trade-order-logistics-tracking__body">
                <div className="trade-order-logistics-tracking__left">
                    {waybill ? (
                        <Timeline
                            items={visibleSteps.map(([state, tip], index) => ({
                                color: index === 0 ? "#1777ff" : "#d9d9d9",
                                children: (
                                    <span className={index === 0 ? "text-blue-600" : "text-gray-600"}>
                                        [{WAYBILL_STATE.getIntroByCode(state)}] {tip}
                                    </span>
                                )
                            }))}
                        />
                    ) : (
                        <div className="trade-order-logistics-tracking__empty">
                            <img alt="notData" src={notDataImage} />
                            <span>暂未关联运单数据</span>
                        </div>
                    )}
                </div>
                <MapVideoWrap vo={mapShipVo} transportOrderDetailsShip={deliveryVo?.transportOrderDetailsShip} />
            </div>
        </section>
    );
}
